import React, { useState } from 'react';
import { ClientCreate } from '../../api/models/ClientCreate';
import { ManagementOauthApi } from '../../api/apis';
import { apiClient } from '../../services/apiClient';
import { clientService } from '../../services/clientService';
import { useI18n } from '../../contexts/I18nContext';
import './CreateClientModal.css';

interface CreateClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onClientCreated: (client: ClientCreate) => void;
}

interface FormErrors {
  [key: string]: string;
}

export const CreateClientModal: React.FC<CreateClientModalProps> = ({
  isOpen,
  onClose,
  onClientCreated
}) => {
  const { t } = useI18n();
  const [isLoading, setIsLoading] = useState(false);
  const [clientName, setClientName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const [showHelp, setShowHelp] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Required field validation
    if (!clientName || clientName.trim() === '') {
      newErrors.name = t('errors.fieldRequired');
    } else if (clientName.trim().length < 2) {
      newErrors.name = 'Client name must be at least 2 characters';
    } else if (clientName.trim().length > 256) {
      newErrors.name = 'Client name must not exceed 256 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const managementApi = new ManagementOauthApi(apiClient.getConfiguration());

      const clientData: ClientCreate = {
        name: clientName.trim()
      };

      await managementApi.apiAddClientV2V2ClientPost({
        clientCreate: clientData
      });

      // Bust the in-memory client cache so subsequent modals
      // (AddHardwareTypeModal, CreateConfigTemplateModal, etc.) see
      // the newly created client without the 5-minute TTL wait.
      clientService.clearCache();

      // Call the callback with the created client
      onClientCreated(clientData);

      // Reset form
      setClientName('');
      setErrors({});
      setShowHelp(false);

      // Close modal
      onClose();
    } catch (err: any) {
      console.error('[CREATE CLIENT] Error creating client:', err);
      console.error('[CREATE CLIENT] Full error response:', err.response);
      console.error('[CREATE CLIENT] Error response data:', err.response?.data);

      // Extract detailed error message
      let errorMessage = t('errors.failedToSave');

      if (err.response?.data) {
        const responseData = err.response.data;

        if (responseData.detail) {
          if (Array.isArray(responseData.detail)) {
            // Format validation errors
            errorMessage = responseData.detail.map((error: any) => {
              const field = error.loc?.join('.') || 'field';
              return `${field}: ${error.msg}`;
            }).join(', ');
          } else if (typeof responseData.detail === 'string') {
            // Check for duplicate client name
            if (responseData.detail.includes('duplicate key') ||
                responseData.detail.includes('already exists')) {
              errorMessage = `Client name '${clientName}' already exists. Please use a different name.`;
            }
            // Generic database error
            else if (responseData.detail.includes('psycopg2')) {
              errorMessage = `Database error: ${responseData.detail.split('\n')[0]}`;
            }
            // Use raw message
            else {
              errorMessage = responseData.detail;
            }
          }
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setClientName('');
      setErrors({});
      setError(null);
      setShowHelp(false);
      onClose();
    }
  };

  // Field Help Component
  const FieldHelpText: React.FC<{ value: string }> = ({ value }) => {
    if (!showHelp) return null;

    const isValid = value.trim().length >= 2 && value.trim().length <= 256;

    return (
      <div className={`field-help ${isValid ? 'valid' : 'invalid'}`}>
        <div className="help-description">
          Client name identifier for organization or group
        </div>
        <div className="help-validation">
          {isValid ? '✓ Valid client name' : '⚠ Must be 2-256 characters'}
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content create-client-modal" data-testid="modal-create-client" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="header-title-section">
            <h2>🏢 {t('clients.addClient')}</h2>
          </div>
          <div className="header-controls">
            <div className="help-toggle">
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={showHelp}
                  onChange={(e) => setShowHelp(e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
              <span className="toggle-label">{t('common.fieldHelp')}</span>
            </div>
            <button
              className="close-button"
              data-testid="btn-modal-close"
              onClick={handleClose}
              disabled={isLoading}
              aria-label={t('common.close')}
            >
              ×
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && (
              <div className="error-banner">
                <span>{error}</span>
                <button
                  type="button"
                  onClick={() => setError(null)}
                  aria-label={t('common.dismissError')}
                >
                  ×
                </button>
              </div>
            )}

            <div className="form-section">
              <h4>{t('clients.information')}</h4>

              <div className="form-group">
                <label htmlFor="clientName" className="required">
                  {t('clients.clientName')} <span className="required-asterisk">*</span>
                </label>
                <input
                  type="text"
                  id="clientName"
                  data-testid="field-client-name"
                  value={clientName}
                  onChange={(e) => {
                    setClientName(e.target.value);
                    if (errors.name) {
                      setErrors({ ...errors, name: '' });
                    }
                  }}
                  className={errors.name ? 'error' : ''}
                  placeholder={t('clients.namePlaceholderDetailed')}
                  disabled={isLoading}
                  autoFocus
                  maxLength={256}
                />
                {errors.name && (
                  <span className="error-text">{errors.name}</span>
                )}
                <FieldHelpText value={clientName} />
              </div>

              <div className="form-help-text">
                <p><strong>ℹ️ About Clients:</strong> A client represents an organization or group that manages multiple devices. All devices must be associated with a client. Create clients before adding devices.</p>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              data-testid="btn-cancel"
              onClick={handleClose}
              disabled={isLoading}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              data-testid="btn-submit"
              disabled={isLoading}
            >
              {isLoading ? t('common.loading') : t('common.create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
